import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

export const SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
] as const;

export const MARITAL_STATUS_OPTIONS = [
  { value: 'Never married', label: 'Never married' },
  { value: 'Married', label: 'Married' },
  { value: 'Partnered', label: 'Partnered' },
  { value: 'Separated', label: 'Separated' },
  { value: 'Divorced', label: 'Divorced' },
  { value: 'Widowed', label: 'Widowed' },
] as const;

export const quickPlanPersonSchema = z.object({
  firstName: z.string().trim().min(3, 'First name must be at least 3 characters'),
  lastName: z.string().trim().min(2, 'Last name must be at least 2 characters'),
  birthDate: z.date({ required_error: 'Birth date is required' }),
  sex: z.enum(['male', 'female'], {
    required_error: 'Please select a sex',
  }),
  smokingStatus: z.enum(['never', 'former', 'current'], {
    required_error: 'Please select smoking status',
  }),
  zipCode: z
    .string()
    .min(1, 'Zip code is required')
    .regex(/^\d{5}$/, 'Must be a 5-digit zip code'),
  maritalStatus: z.string().min(1, 'Marital status is required'),
});

export type QuickPlanPersonValues = z.infer<typeof quickPlanPersonSchema>;

export const quickPlanSchema = quickPlanPersonSchema
  .extend({
    hasPartner: z.boolean().default(false),
    partner: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.hasPartner) {
      if (!data.partner) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Partner details are required',
          path: ['partner'],
        });
      } else {
        const result = quickPlanPersonSchema.safeParse(data.partner);
        if (!result.success) {
          for (const issue of result.error.issues) {
            ctx.addIssue({ ...issue, path: ['partner', ...issue.path] });
          }
        }
      }
    }
  });

export type QuickPlanFormValues = z.infer<typeof quickPlanSchema>;

interface UseQuickPlanFormOptions {
  onSubmit: (data: QuickPlanFormValues) => void;
}

const PARTNER_ELIGIBLE_STATUSES = ['Married', 'Partnered'];

export function useQuickPlanForm({ onSubmit }: UseQuickPlanFormOptions) {
  const form = useForm<QuickPlanFormValues>({
    resolver: zodResolver(quickPlanSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      zipCode: '',
      maritalStatus: '',
      hasPartner: false,
    },
  });

  const hasPartner = form.watch('hasPartner');
  const maritalStatus = form.watch('maritalStatus');
  const canAddPartner = PARTNER_ELIGIBLE_STATUSES.includes(maritalStatus);
  const partnerLabel = maritalStatus === 'Married' ? 'spouse' : 'partner';

  function clearPartner() {
    const { partner, ...values } = form.getValues();
    form.reset({ ...values, hasPartner: false }, { keepDirty: true, keepTouched: true });
  }

  function handleTogglePartner(checked: boolean) {
    if (checked) {
      form.setValue('hasPartner', true, { shouldValidate: true });
      form.setValue('partner.maritalStatus', maritalStatus, { shouldValidate: true });
    } else {
      clearPartner();
    }
  }

  function handleMaritalStatusChange(value: string) {
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value) && hasPartner) {
      clearPartner();
    } else if (hasPartner) {
      form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    }
  }

  function handlePartnerMaritalStatusChange(value: string) {
    form.setValue('partner.maritalStatus', value, { shouldValidate: true });
    form.setValue('maritalStatus', value, { shouldValidate: true });
    if (!PARTNER_ELIGIBLE_STATUSES.includes(value)) {
      clearPartner();
    }
  }

  return {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit: form.handleSubmit(onSubmit),
    isValid: form.formState.isValid,
  };
}
